<script setup lang="ts">
import type { Form, FormErrorEvent, FormSubmitEvent } from '#ui/types';
import { z } from 'zod';

import {
    type BackendErrorEntity,
    BusPurchaseQtyOption,
    BusSaleQtyOption,
    type ErrorType,
    OperatorType,
    type SalesPredictionDto,
    type UpdateAccountCompanyBodyDto,
    useApiCreateSalesPrediction,
    useApiUpdateAccountCompany,
} from '~/apiClient';
import { AUTOCOMPLETE_OFF } from '~/constants/form';

// EVENTS
const emit = defineEmits(['next']);

// COMPOSABLES
const { logError, logInfo } = useLogs();
const { notifyBackendError, notifyGenericError } = useNotification();
const { tt } = useTypedI18n();
const { getFormErrors } = useBackendErrors();
const { data: session } = useAuthUtils();
const { mutateAsync: mutateCompany, isPending: isPendingCompany } = useApiUpdateAccountCompany();
const { mutateAsync: mutateSalesPrediction, isPending: isPendingSalesPrediction } = useApiCreateSalesPrediction();

// DATA
const formSchema = z.object({
    type: z.enum(OperatorType, {
        error: tt('validation.msg.required', { field: tt('signup.fields.type.label') }),
    }),
    statsBusSaleQty: z.enum(BusSaleQtyOption).or(z.undefined()),
    statsBusPurchaseQty: z.enum(BusPurchaseQtyOption).or(z.undefined()),
});
type FormSchema = typeof formSchema;
type FormData = z.infer<FormSchema>;
const formData = reactive<Partial<FormData>>({
    type: OperatorType.PeopleTransportOperator,
    statsBusSaleQty: undefined,
    statsBusPurchaseQty: undefined,
});
const form = useTemplateRef<Form<FormSchema>>('form');

const twoYearsPeriod = `${new Date().getFullYear()}/${new Date().getFullYear() + 1}`;
const isPending = computed(() => isPendingCompany.value || isPendingSalesPrediction.value);

// HANDLERS
const onSubmit = async (event: FormSubmitEvent<FormData>): Promise<void> => {
    const { data } = event;
    const { id: userId, companyId } = session.value?.user ?? {};

    const companyBody: UpdateAccountCompanyBodyDto = {
        operatorType: data.type,
    };

    Promise.resolve(companyBody.operatorType && mutateCompany({ data: companyBody }))
        .then(() => {
            if (!userId || !companyId) {
                logError('Error during signup onboarding salesPrediction step: user or company id undefined', {
                    userId,
                    companyId,
                });

                return;
            }

            if (!data.statsBusSaleQty && !data.statsBusPurchaseQty) return;

            const salesPredictionBody: SalesPredictionDto = {
                userId,
                companyId,
                stats: {
                    busSaleQty: data.statsBusSaleQty,
                    busPurchaseQty: data.statsBusPurchaseQty,
                    yearStart: new Date().getFullYear(),
                    yearEnd: new Date().getFullYear() + 1,
                },
            };

            return mutateSalesPrediction({ data: salesPredictionBody }).catch((apiError: ErrorType<BackendErrorEntity>) => {
                logError('Error during signup onboarding additional information step', {
                    apiError: apiError.data,
                    userId,
                    formData: {
                        ...data,
                    },
                });
            });
        })
        .then(() => {
            emit('next');
        })
        .catch((apiError: ErrorType<BackendErrorEntity>) => {
            logError('Error during signup onboarding company step', {
                apiError: apiError.data,
                userId: session.value?.user?.id,
                formData: {
                    ...data,
                },
            });

            if (!apiError.data?.error) {
                notifyGenericError();

                return;
            }

            const { error } = apiError.data;
            notifyBackendError({
                error,
            });

            const formErrors = getFormErrors<UpdateAccountCompanyBodyDto, FormData>(apiError, {
                operatorType: 'type',
            });
            form.value?.setErrors(formErrors);
            scrollToFormError();
        });
};

const onError = (errorEvent: FormErrorEvent): void => {
    logInfo('Error before signup onboarding additional information step', {
        formErrors: errorEvent.errors,
        formData,
    });

    if (!errorEvent.errors.length) return;

    scrollToFormError();
};
</script>

<template>
    <UForm ref="form" class="flex flex-col gap-4" :state="formData" :schema="formSchema" @submit="onSubmit" @error="onError">
        <p class="text-center">{{ tt('signup.onboarding.info.subtitle') }}</p>

        <UFormField :label="tt('signup.fields.type.label')" name="type" required>
            <SignupUserType v-model="formData.type" :disabled="isPendingCompany" :autocomplete="AUTOCOMPLETE_OFF" />
        </UFormField>

        <USeparator />

        <UFormField :label="tt('signup.fields.stats.sales', { year: twoYearsPeriod })" name="statsBusSaleQty">
            <SignupStatsSales v-model="formData.statsBusSaleQty" :autocomplete="AUTOCOMPLETE_OFF" />
        </UFormField>

        <UFormField :label="tt('signup.fields.stats.purchase', { year: twoYearsPeriod })" name="statsBusPurchaseQty">
            <SignupStatsPurchase v-model="formData.statsBusPurchaseQty" :autocomplete="AUTOCOMPLETE_OFF" />
        </UFormField>

        <UButton :label="tt('signup.onboarding.confirm')" class="mt-4" type="submit" size="xl" :loading="isPending" block />
        <UButton :label="tt('signup.onboarding.skip')" color="neutral" variant="ghost" size="xl" :disabled="isPending" block @click="emit('next')" />
    </UForm>
</template>
